import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  SummaryTypes,
  useGetSummaryPromptMutation,
  useProcessAudioMutation,
  useProcessTextMutation,
  useUpdateTranscriptionMutation,
  useShareToSlackMutation,
  useShareToSalesforceMutation
} from "../store/apiSlice";
import { showErrorToast, showWarningToast, showSuccessToast } from "../toastUtils";
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import MicIcon from "@mui/icons-material/Mic";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LoadingPage from "../common/LoadingPage";
import { getErrorMessage } from "../error-helpers/errorMessage";
import JsPDF from "jspdf";
import { formatElapsedTime, copyToClipboard } from "../common/commonFuncs";
import logo from "../images/tranzcribe-logo-text-black.png";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import {
  countdownBoxStyles,
  countdownTypographyStyles,
  tabStyles,
} from "./styles";
import { logAnalyticsEvent } from "../config/firebase";
import { logPosthogEvent } from "../common/posthogUtils";
import { useAuth } from "@clerk/clerk-react";
import { API_URL } from "../config";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faComment,
  faFile,
  faChartBar,
  faCalendar,
  faBookmark,
  faStickyNote,
  faAddressCard,
  faBuilding,
  faCircle,
  faClock,
  faClone,
  faCompass,
  faCopyright,
  faCreditCard,
  faFaceSmile,
  faFileAudio,
  faFileCode,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faFileZipper,
  faFolderOpen,
  faHdd,
  faHeart,
  faHourglass,
  faImage,
  faKeyboard,
  faLightbulb,
  faMap,
  faMoon,
  faNewspaper,
  faPaperPlane,
  faPaste,
  faPenToSquare,
  faRectangleList,
  faStar,
  faSun,
  faThumbsUp,
  faUser
} from '@fortawesome/free-regular-svg-icons';
import {
  faTag,
  faGlobe,
  faAnchor,
  faArrowUpRightDots,
  faBell,
  faBolt,
  faBrain,
  faBriefcase,
  faBullhorn,
  faCamera,
  faCartShopping,
  faChartPie,
  faChess,
  faCode,
  faCoffee,
  faCogs,
  faCoins,
  faDatabase,
  faDiagramProject,
  faEarthAmericas,
  faFileAlt,
  faFire,
  faFlag,
  faFlask,
  faGaugeHigh,
  faGears,
  faGem,
  faGraduationCap,
  faHeadset,
  faHouse,
  faInfo,
  faLink,
  faMagnifyingGlass,
  faMapLocation,
  faMicrochip,
  faMicrophone,
  faMusic,
  faPalette,
  faPenRuler,
  faPeopleGroup,
  faPhone,
  faPrint,
  faPuzzlePiece,
  faRobot,
  faRocket,
  faScroll,
  faShield,
  faShoppingBag,
  faSignal,
  faSitemap,
  faStopwatch,
  faTableList,
  faTerminal,
  faToolbox,
  faTrophy,
  faUserGear,
  faVideo,
  faWifi
} from '@fortawesome/free-solid-svg-icons';

import SlackIcon from "./icons/SlackIcon";
import SalesforceIcon from "./icons/SalesforceIcon";
import SummarizationDisplayTab from "./SummarizationDisplayTab";
import { useNavigate } from "react-router-dom";
import { useGetUserAttributesQuery } from "../store/userApi";
import Header from "./common/Header";
import Footer from "./common/Footer";
import PromptTab from "./prompt-tab/PromptTab";
import { fetchGlobalPrompts, SubPrompt } from "../store/promptsSlice";
import { AppDispatch } from "../store/store";
import { useAppSettings } from "../contexts/AppSettingsContext";

const AudioTranscriber: React.FC = () => {
  const { settings, getLogoUrl } = useAppSettings();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [, setAudioUrl] = useState<string | null>(null);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [language] = useState<string>("en");
  const [showCompletedIcon, setShowCompletedIcon] = useState(false);
  const elapsedTimeRef = useRef(0);
  const [summarizedText, setSummarizedText] = useState<string | null>(null);
  const [shareToSlackLoading, setShareToSlackLoading] = useState(false);
  const [shareToSalesforceLoading, setShareToSalesforceLoading] = useState(false);
  const [salesforceError, setSalesforceError] = useState<string | null>(null);
  const [isEditingSalesforceText, setIsEditingSalesforceText] = useState(false);
  const [editedSalesforceText, setEditedSalesforceText] = useState<string>("");
  const [salesforceTextSaved, setSalesforceTextSaved] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState<'Slack Integration' | 'Salesforce Integration' | null>(null);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    message: '',
    feature: ''
  });
  const [sendingContactForm, setSendingContactForm] = useState(false);

  // Check feature flags from environment variables
  const isSlackEnabled = process.env.REACT_APP_ENABLE_SLACK === 'true';
  const isSalesforceEnabled = process.env.REACT_APP_ENABLE_SALESFORCE === 'true';

  // New state for input mode (audio vs text)
  const [isTextMode, setIsTextMode] = useState(() => {
    const savedMode = localStorage.getItem('tranzcribe-input-mode');
    return savedMode === 'text';
  });

  // New state for direct text input
  const [directTextInput, setDirectTextInput] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioBlobRef = useRef<Blob | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const [getSummaryPrompt, { isLoading: loadingSummary }] =
    useGetSummaryPromptMutation();
  const [processAudio, { isLoading: isProcessingAudio }] = useProcessAudioMutation();
  const [processText, { isLoading: isProcessingText }] = useProcessTextMutation();
  const [updateTranscription, { isLoading: isUpdatingTranscription }] = useUpdateTranscriptionMutation();
  const [shareToSlack] = useShareToSlackMutation();
  const [shareToSalesforce] = useShareToSalesforceMutation();
  const { refetch: refetchUserData } = useGetUserAttributesQuery();
  const { getToken } = useAuth();

  const user = useSelector((state: RootState) => state.user.user);
  const globalPrompts = useSelector((state: RootState) => state.globalPrompts.prompts);
  const globalPromptsLoading = useSelector((state: RootState) => state.globalPrompts.loading);
  const dispatch = useDispatch<AppDispatch>();

  // Check if the current domain is carehub.tranzcribe.com
  const isCareHubDomain = window.location.hostname === 'carehub.tranzcribe.com';

  // Fetch global prompts on component mount
  useEffect(() => {
    dispatch(fetchGlobalPrompts());
  }, [dispatch]);

  // Add this effect to refetch user data when the user changes
  useEffect(() => {
    // If we have a user ID or email (from clerk or store), refetch to ensure fresh data
    if (user?.id || user?.email) {
      // Reset component state when user changes
      setHasRecorded(false);
      setTranscribedText(null);
      setActiveTab(0);
      setSummarizedText(null);

      // Refetch user data
      refetchUserData();
    }
  }, [user?.id, user?.email, refetchUserData]);

  // Add this effect to reset Salesforce editing when transcribedText changes
  useEffect(() => {
    if (transcribedText) {
      setEditedSalesforceText(transcribedText);
      setIsEditingSalesforceText(false);
      setSalesforceTextSaved(false);
      setSalesforceError(null);
    }
  }, [transcribedText]);

  // Reset Salesforce error when tab changes
  useEffect(() => {
    setSalesforceError(null);
  }, [activeTab]);

  // Add this effect to save the input mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('tranzcribe-input-mode', isTextMode ? 'text' : 'audio');
  }, [isTextMode]);

  // Add state to track transcription ID
  const [transcriptionId, setTranscriptionId] = useState<string | null>(null);

  const handleProcessAudio = useCallback(async () => {
    if (!audioBlobRef.current) {
      showWarningToast("No audio recorded. Please record audio first.");
      return;
    }
    const formData = new FormData();
    formData.append("audio", audioBlobRef.current, "recording");
    formData.append("language", language);
    formData.append("videoLength", elapsedTimeRef.current.toString());
    try {
      const response = await processAudio(formData).unwrap();
      setTranscribedText(response.transcribedText);

      // Store transcription ID from response if available
      if (response.transcriptionId) {
        setTranscriptionId(response.transcriptionId);
      }

      const summaryText = await getSummaryPrompt({
        text: response.transcribedText,
        type: SummaryTypes.SUMMARY,
      }).unwrap();
      setSummarizedText(summaryText.gptTextResponse);

      // Refetch user data
      refetchUserData();

    } catch (error) {
      // Extract more detailed error message if available
      let errorMessage = 'Failed to process audio';

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error as any;
        if (errorData.data?.message) {
          errorMessage = errorData.data.message;
        } else if (errorData.data?.error === 'FILE_TOO_LARGE') {
          errorMessage = 'Audio file is too large. Please record a shorter audio.';
        }
      }

      showErrorToast(`${errorMessage}: ${getErrorMessage(error)}`);
    }
  }, [processAudio, language, getSummaryPrompt, refetchUserData]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      let mimeType: string = "";
      if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/mpeg")) {
        mimeType = "audio/mpeg";
      } else {
        showErrorToast("No supported audio format found for recording.");
        return;
      }
      logAnalyticsEvent("startRecording", {
        mimeType,
      });
      logPosthogEvent(
        "startRecording",
        {
          mimeType,
        },
        user?.email ?? 'anonymous-user',
      );

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioBlobRef.current = audioBlob;

        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioUrl);
        audioChunksRef.current = [];

        // Close the microphone by stopping all tracks
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }

        // Process the audio after it's ready
        setTimeout(() => {
          handleProcessAudio();
        }, 500);
      };

      // Clear any existing recording
      audioChunksRef.current = [];
      audioBlobRef.current = null;

      // This will be executed immediately since countdown is handled separately
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setHasRecorded(true);
      startRecordingTimer();

    } catch (error: any) {
      // Handle specific permission errors
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Show a more helpful error message with instructions to reset permissions
        const browserName = getBrowserName();
        const permissionInstructions = getPermissionInstructions(browserName);

        showErrorToast(
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Microphone access denied
            </Typography>
            <Typography variant="body2">
              Please allow microphone access to use the recording feature.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {permissionInstructions}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              onClick={requestPermissionAgain}
            >
              Try Again
            </Button>
          </Box>,
          {
            autoClose: false, // Don't auto close this important message
          }
        );

        console.error('Microphone permission denied:', error);
      } else {
        // Handle other recording errors
        showErrorToast(`Failed to start recording: ${getErrorMessage(error)}`);
        console.error('Recording error:', error);
      }
    }
  }, [user?.email, handleProcessAudio]);

  // Function to force a new permission request by resetting media devices
  const requestPermissionAgain = () => {
    // Force browsers to show the permission dialog again
    // This works by using an additional constraint (video) and then immediately stopping it
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        .then(stream => {
          // Immediately stop all tracks to avoid using the camera
          stream.getTracks().forEach(track => track.stop());

          // Now ask for just audio again, which should trigger the permission dialog
          startCountdown();
        })
        .catch(error => {
          console.error('Error requesting permission again:', error);
          // If this fails, guide the user to reset in browser settings
          const browserName = getBrowserName();
          showWarningToast(`Please manually reset permissions in your ${browserName} settings.`);
        });
    }
  };

  const startCountdown = () => {
    setCountdown(3);
  };

  useEffect(() => {
    if (countdown === null) return;

    if (countdown > 0) {
      const timer = setTimeout(
        () => setCountdown((prev) => (prev ?? 0) - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }

    if (countdown === 0) {
      setCountdown(null); // Clear countdown first to prevent UI issues
      startRecording();
    }
  }, [countdown, startRecording]);

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      clearRecordingTimer();
      setIsRecording(false);

      // Processing is now handled in mediaRecorder.onstop
    }

    // Close the microphone by stopping all tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const startRecordingTimer = () => {
    // Reset timer first
    elapsedTimeRef.current = 0;
    setElapsedTime(0);

    recordingTimerRef.current = setInterval(() => {
      elapsedTimeRef.current += 1;
      setElapsedTime(elapsedTimeRef.current); // This updates UI without affecting `elapsedTimeRef`

      // Check for 5-minute warning (300 seconds)
      if (elapsedTimeRef.current === 300) {
        showWarningToast(
          "You've been recording for 5 minutes. The maximum recording duration is 10 minutes."
        );
      }

      // Check for 10-second warning (590 seconds = 9:50)
      if (elapsedTimeRef.current === 590) {
        showWarningToast(
          "Recording will stop in 10 seconds! Maximum duration of 10 minutes is almost reached."
        );
      }

      // Automatically stop recording at 10 minutes (600 seconds)
      if (elapsedTimeRef.current >= 600) {
        console.log('Maximum recording duration reached, stopping recording');
        stopRecording();
        showWarningToast(
          "Recording stopped. Maximum duration of 10 minutes reached."
        );
      }
    }, 1000);
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleDownload = async (text: string | null, filename: string) => {
    if (!text) return;

    const doc = new JsPDF();
    doc.setFontSize(12);

    // Add the logo at the top (centered)
    const imgWidth = 75; // Adjust width as needed
    let imgHeight = 35; // Default height, will be updated based on aspect ratio
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = (pageWidth - imgWidth) / 2; // Center the image horizontally

    // Get the customized logo URL
    const customLogoUrl = getLogoUrl();

    try {
      // Create a temporary canvas to convert the image to DataURL
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Load the image
      const imageLoadPromise = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';  // This is important for CORS

        img.onload = () => {
          // Set canvas dimensions to match the image
          canvas.width = img.width;
          canvas.height = img.height;

          // Calculate the height to maintain aspect ratio
          const calculatedImgHeight = imgWidth * (img.height / img.width);

          // Draw the image on canvas
          ctx?.drawImage(img, 0, 0);

          try {
            // Convert to DataURL and add to PDF
            const dataUrl = canvas.toDataURL('image/png');
            doc.addImage(dataUrl, 'PNG', marginX, 10, imgWidth, calculatedImgHeight);

            // Update imgHeight for text positioning
            imgHeight = calculatedImgHeight;

            resolve(true);
          } catch (error) {
            console.error('Error converting image to DataURL:', error);
            reject(error);
          }
        };

        img.onerror = (error) => {
          console.error('Error loading logo image:', error);
          reject(error);
        };

        // Set the src to start loading the image
        img.src = customLogoUrl;
      });

      // Wait for the image to load and be added to the PDF
      await imageLoadPromise;
    } catch (error) {
      console.error('Error processing logo for PDF:', error);
      // Fallback to default logo if there's an error
      imgHeight = 35; // Reset to default height if there's an error
      doc.addImage(logo, 'PNG', marginX, 10, imgWidth, imgHeight);
    }

    // Add the text below the logo
    const lines = doc.splitTextToSize(text, 180);
    doc.text(lines, 10, imgHeight + 20);

    // Add black outline around the PDF page
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setLineWidth(0.2);
    doc.setDrawColor(0, 0, 0); // Black color for the outline
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10); // Rectangle outline

    logAnalyticsEvent("handleDownload", {
      filename,
    });
    doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Check if user is trying to access Slack tab when disabled
    const slackTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;
    const salesforceTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 3;

    if (!isCareHubDomain) {
      if (newValue === slackTabIndex && !isSlackEnabled) {
        setBlockedFeature('Slack Integration');
        setUpgradeModalOpen(true);
        return; // Don't change tab
      }

      if (newValue === salesforceTabIndex && !isSalesforceEnabled) {
        setBlockedFeature('Salesforce Integration');
        setUpgradeModalOpen(true);
        return; // Don't change tab
      }
    }

    logAnalyticsEvent("handlePromptChange", {
      activeTab: newValue,
    });
    setActiveTab(newValue);
    // Don't reset the summary text when switching tabs, to ensure it's still visible when coming back
  };

  const handleMicClick = () => {
    if (isTextMode) {
      // In text mode, reset the text field
      setDirectTextInput('');
      setHasRecorded(false);
      setTranscribedText(null);
      setActiveTab(0);
      setSummarizedText(null);
    } else {
      // Original audio recording logic
      if (hasRecorded) {
        setHasRecorded(false);
        setTranscribedText(null);
        setActiveTab(0);
        setSummarizedText(null);
        startCountdown();
      } else {
        startCountdown();
      }
    }
  };

  // Updated styles for the countdown element
  const updatedCountdownBoxStyles = {
    ...countdownBoxStyles,
    backgroundColor: `rgba(${settings.primaryColor ? parseInt(settings.primaryColor.slice(1, 3), 16) : 25}, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(3, 5), 16) : 118
      }, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(5, 7), 16) : 210}, 0.8)`,
    color: "white",
    boxShadow: `0px 0px 15px rgba(${settings.primaryColor ? parseInt(settings.primaryColor.slice(1, 3), 16) : 25}, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(3, 5), 16) : 118
      }, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(5, 7), 16) : 210}, 0.7)`,
  };

  const updatedCountdownTypographyStyles = {
    ...countdownTypographyStyles,
    color: "white",
  };

  // Handle sharing to Slack
  const handleShareToSlack = async () => {
    if (isCareHubDomain) return; // Don't allow Slack sharing on carehub domain

    // Check if feature is enabled
    if (!isSlackEnabled) {
      setUpgradeModalOpen(true);
      return;
    }

    if (!transcribedText) {
      showWarningToast("No transcription available to share.");
      return;
    }

    try {
      setShareToSlackLoading(true);
      await shareToSlack({ text: transcribedText }).unwrap();
      showSuccessToast("Transcription successfully shared to Slack!");
    } catch (error) {
      showErrorToast(`Failed to share to Slack: ${getErrorMessage(error)}`);
    } finally {
      setShareToSlackLoading(false);
    }
  };

  // Handle sharing to Salesforce
  const handleShareToSalesforce = async () => {
    if (isCareHubDomain) return; // Don't allow Salesforce sharing on carehub domain

    // Check if feature is enabled
    if (!isSalesforceEnabled) {
      setUpgradeModalOpen(true);
      return;
    }

    if (!transcribedText) {
      setSalesforceError("No transcription available to share.");
      return;
    }

    try {
      setShareToSalesforceLoading(true);
      setSalesforceError(null);

      const response = await shareToSalesforce({
        text: transcribedText,
        userEmail: user?.email || 'anonymous@example.com'
      }).unwrap();

      showSuccessToast("Transcription successfully shared to Salesforce!");
    } catch (error: any) {
      console.error('Error sharing to Salesforce:', error);

      // Handle specific error cases
      if (error.status === 409 && error.data?.errorCode === 'DUPLICATE_LEAD') {
        setSalesforceError('This information already exists in Salesforce. Duplicate lead detected.');
      } else if (error.data?.message) {
        setSalesforceError(error.data.message);
      } else {
        setSalesforceError('Failed to share to Salesforce. Please try again later.');
      }
    } finally {
      setShareToSalesforceLoading(false);
    }
  };

  // Add handler for saving edited transcript
  const handleSaveEditedTranscript = async () => {
    if (!transcribedText || !editedSalesforceText) {
      setSalesforceError("No content to save");
      return;
    }

    try {
      setSalesforceTextSaved(true);
      setIsEditingSalesforceText(false);
      showSuccessToast("Edited transcript saved successfully!");
    } catch (error: any) {
      console.error('Error saving edited transcript:', error);
      setSalesforceError(error.message || 'Failed to save edited transcript');
    }
  };

  // Modify the handleTextSubmit function to store the transcription ID
  const handleTextSubmit = async () => {
    if (!directTextInput.trim()) {
      showWarningToast("Please enter some text before submitting.");
      return;
    }

    try {
      // Log analytics for text submission
      logAnalyticsEvent("textSubmission", {
        textLength: directTextInput.length,
      });
      logPosthogEvent(
        "textSubmission",
        {
          textLength: directTextInput.length,
        },
        user?.email ?? 'anonymous-user',
      );

      // Process and save the text using the backend API
      const duration = 0; // No actual duration for text submissions
      const response = await processText({
        text: directTextInput,
        duration: duration
      }).unwrap();

      setHasRecorded(true);
      setTranscribedText(response.transcribedText);

      // Store transcription ID from response if available
      if (response.transcriptionId) {
        setTranscriptionId(response.transcriptionId);
      }

      // Get summary of the text
      const summaryText = await getSummaryPrompt({
        text: response.transcribedText,
        type: SummaryTypes.SUMMARY,
      }).unwrap();
      setSummarizedText(summaryText.gptTextResponse);

      // Refetch user data
      refetchUserData();

      // Show success indicator
      setShowCompletedIcon(true);
      setTimeout(() => setShowCompletedIcon(false), 2000);

    } catch (error) {
      showErrorToast(`Failed to process text: ${getErrorMessage(error)}`);
    }
  };

  // Handle mode toggle
  const handleModeToggle = () => {
    setIsTextMode(!isTextMode);

    // Reset state when changing modes
    setHasRecorded(false);
    setTranscribedText(null);
    setActiveTab(0);
    setSummarizedText(null);
    setDirectTextInput('');
  };

  // Helper function to get the correct icon based on the prompt's iconType
  const getPromptIcon = (iconType: string | undefined) => {
    // Default to 'email' if iconType is undefined or null
    const icon = iconType || 'email';

    switch (icon) {
      // Communication
      case 'email':
        return <FontAwesomeIcon icon={faEnvelope} size="lg" />;
      case 'message':
        return <FontAwesomeIcon icon={faComment} size="lg" />;
      case 'paper-plane':
        return <FontAwesomeIcon icon={faPaperPlane} size="lg" />;
      case 'phone':
        return <FontAwesomeIcon icon={faPhone} size="lg" />;
      case 'bullhorn':
        return <FontAwesomeIcon icon={faBullhorn} size="lg" />;
      case 'headset':
        return <FontAwesomeIcon icon={faHeadset} size="lg" />;
      case 'microphone':
        return <FontAwesomeIcon icon={faMicrophone} size="lg" />;
      case 'video':
        return <FontAwesomeIcon icon={faVideo} size="lg" />;

      // Documents
      case 'document':
        return <FontAwesomeIcon icon={faFile} size="lg" />;
      case 'file-pdf':
        return <FontAwesomeIcon icon={faFilePdf} size="lg" />;
      case 'file-word':
        return <FontAwesomeIcon icon={faFileWord} size="lg" />;
      case 'file-excel':
        return <FontAwesomeIcon icon={faFileExcel} size="lg" />;
      case 'file-powerpoint':
        return <FontAwesomeIcon icon={faFilePowerpoint} size="lg" />;
      case 'file-image':
        return <FontAwesomeIcon icon={faFileImage} size="lg" />;
      case 'file-audio':
        return <FontAwesomeIcon icon={faFileAudio} size="lg" />;
      case 'file-video':
        return <FontAwesomeIcon icon={faFileVideo} size="lg" />;
      case 'file-code':
        return <FontAwesomeIcon icon={faFileCode} size="lg" />;
      case 'file-zipper':
        return <FontAwesomeIcon icon={faFileZipper} size="lg" />;
      case 'newspaper':
        return <FontAwesomeIcon icon={faNewspaper} size="lg" />;
      case 'folder':
        return <FontAwesomeIcon icon={faFolderOpen} size="lg" />;
      case 'clipboard':
        return <FontAwesomeIcon icon={faPaste} size="lg" />;
      case 'clone':
        return <FontAwesomeIcon icon={faClone} size="lg" />;

      // Analytics & Data
      case 'chart':
        return <FontAwesomeIcon icon={faChartBar} size="lg" />;
      case 'chart-pie':
        return <FontAwesomeIcon icon={faChartPie} size="lg" />;
      case 'signal':
        return <FontAwesomeIcon icon={faSignal} size="lg" />;
      case 'gauge':
        return <FontAwesomeIcon icon={faGaugeHigh} size="lg" />;
      case 'database':
        return <FontAwesomeIcon icon={faDatabase} size="lg" />;
      case 'table':
        return <FontAwesomeIcon icon={faTableList} size="lg" />;
      case 'sitemap':
        return <FontAwesomeIcon icon={faSitemap} size="lg" />;
      case 'diagram':
        return <FontAwesomeIcon icon={faDiagramProject} size="lg" />;
      case 'growth':
        return <FontAwesomeIcon icon={faArrowUpRightDots} size="lg" />;

      // Time & Planning
      case 'calendar':
        return <FontAwesomeIcon icon={faCalendar} size="lg" />;
      case 'clock':
        return <FontAwesomeIcon icon={faClock} size="lg" />;
      case 'hourglass':
        return <FontAwesomeIcon icon={faHourglass} size="lg" />;
      case 'stopwatch':
        return <FontAwesomeIcon icon={faStopwatch} size="lg" />;

      // Locations & Travel
      case 'globe':
        return <FontAwesomeIcon icon={faGlobe} size="lg" />;
      case 'earth':
        return <FontAwesomeIcon icon={faEarthAmericas} size="lg" />;
      case 'map':
        return <FontAwesomeIcon icon={faMap} size="lg" />;
      case 'location':
        return <FontAwesomeIcon icon={faMapLocation} size="lg" />;
      case 'compass':
        return <FontAwesomeIcon icon={faCompass} size="lg" />;
      case 'anchor':
        return <FontAwesomeIcon icon={faAnchor} size="lg" />;

      // Business & Work
      case 'briefcase':
        return <FontAwesomeIcon icon={faBriefcase} size="lg" />;
      case 'building':
        return <FontAwesomeIcon icon={faBuilding} size="lg" />;
      case 'cogs':
        return <FontAwesomeIcon icon={faCogs} size="lg" />;
      case 'gears':
        return <FontAwesomeIcon icon={faGears} size="lg" />;
      case 'toolbox':
        return <FontAwesomeIcon icon={faToolbox} size="lg" />;
      case 'address-card':
        return <FontAwesomeIcon icon={faAddressCard} size="lg" />;
      case 'user':
        return <FontAwesomeIcon icon={faUser} size="lg" />;
      case 'user-gear':
        return <FontAwesomeIcon icon={faUserGear} size="lg" />;
      case 'people':
        return <FontAwesomeIcon icon={faPeopleGroup} size="lg" />;
      case 'print':
        return <FontAwesomeIcon icon={faPrint} size="lg" />;
      case 'credit-card':
        return <FontAwesomeIcon icon={faCreditCard} size="lg" />;
      case 'coins':
        return <FontAwesomeIcon icon={faCoins} size="lg" />;
      case 'shopping':
        return <FontAwesomeIcon icon={faCartShopping} size="lg" />;
      case 'bag':
        return <FontAwesomeIcon icon={faShoppingBag} size="lg" />;

      // Creative & Content
      case 'lightbulb':
        return <FontAwesomeIcon icon={faLightbulb} size="lg" />;
      case 'brain':
        return <FontAwesomeIcon icon={faBrain} size="lg" />;
      case 'edit':
        return <FontAwesomeIcon icon={faPenToSquare} size="lg" />;
      case 'design':
        return <FontAwesomeIcon icon={faPenRuler} size="lg" />;
      case 'image':
        return <FontAwesomeIcon icon={faImage} size="lg" />;
      case 'camera':
        return <FontAwesomeIcon icon={faCamera} size="lg" />;
      case 'music':
        return <FontAwesomeIcon icon={faMusic} size="lg" />;
      case 'palette':
        return <FontAwesomeIcon icon={faPalette} size="lg" />;
      case 'code':
        return <FontAwesomeIcon icon={faCode} size="lg" />;
      case 'terminal':
        return <FontAwesomeIcon icon={faTerminal} size="lg" />;

      // Markers & Statuses
      case 'bookmark':
        return <FontAwesomeIcon icon={faBookmark} size="lg" />;
      case 'tag':
        return <FontAwesomeIcon icon={faTag} size="lg" />;
      case 'note':
        return <FontAwesomeIcon icon={faStickyNote} size="lg" />;
      case 'list':
        return <FontAwesomeIcon icon={faRectangleList} size="lg" />;
      case 'flag':
        return <FontAwesomeIcon icon={faFlag} size="lg" />;
      case 'star':
        return <FontAwesomeIcon icon={faStar} size="lg" />;
      case 'heart':
        return <FontAwesomeIcon icon={faHeart} size="lg" />;
      case 'thumbs-up':
        return <FontAwesomeIcon icon={faThumbsUp} size="lg" />;
      case 'circle':
        return <FontAwesomeIcon icon={faCircle} size="lg" />;
      case 'bell':
        return <FontAwesomeIcon icon={faBell} size="lg" />;
      case 'info':
        return <FontAwesomeIcon icon={faInfo} size="lg" />;

      // Technology
      case 'robot':
        return <FontAwesomeIcon icon={faRobot} size="lg" />;
      case 'rocket':
        return <FontAwesomeIcon icon={faRocket} size="lg" />;
      case 'bolt':
        return <FontAwesomeIcon icon={faBolt} size="lg" />;
      case 'fire':
        return <FontAwesomeIcon icon={faFire} size="lg" />;
      case 'wifi':
        return <FontAwesomeIcon icon={faWifi} size="lg" />;
      case 'hdd':
        return <FontAwesomeIcon icon={faHdd} size="lg" />;
      case 'microchip':
        return <FontAwesomeIcon icon={faMicrochip} size="lg" />;
      case 'keyboard':
        return <FontAwesomeIcon icon={faKeyboard} size="lg" />;
      case 'link':
        return <FontAwesomeIcon icon={faLink} size="lg" />;
      case 'house':
        return <FontAwesomeIcon icon={faHouse} size="lg" />;

      // Miscellaneous
      case 'gem':
        return <FontAwesomeIcon icon={faGem} size="lg" />;
      case 'trophy':
        return <FontAwesomeIcon icon={faTrophy} size="lg" />;
      case 'graduation':
        return <FontAwesomeIcon icon={faGraduationCap} size="lg" />;
      case 'coffee':
        return <FontAwesomeIcon icon={faCoffee} size="lg" />;
      case 'chess':
        return <FontAwesomeIcon icon={faChess} size="lg" />;
      case 'puzzle':
        return <FontAwesomeIcon icon={faPuzzlePiece} size="lg" />;
      case 'shield':
        return <FontAwesomeIcon icon={faShield} size="lg" />;
      case 'flask':
        return <FontAwesomeIcon icon={faFlask} size="lg" />;
      case 'scroll':
        return <FontAwesomeIcon icon={faScroll} size="lg" />;
      case 'smile':
        return <FontAwesomeIcon icon={faFaceSmile} size="lg" />;
      case 'sun':
        return <FontAwesomeIcon icon={faSun} size="lg" />;
      case 'moon':
        return <FontAwesomeIcon icon={faMoon} size="lg" />;
      case 'search':
        return <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />;
      case 'copyright':
        return <FontAwesomeIcon icon={faCopyright} size="lg" />;

      default:
        return <FontAwesomeIcon icon={faEnvelope} size="lg" />;
    }
  };

  const [selectedPrimaryButton, setSelectedPrimaryButton] = useState<string | null>(null);
  const [selectedSubprompt, setSelectedSubprompt] = useState<SubPrompt | null>(null);
  const [processedPromptText, setProcessedPromptText] = useState<string | null>(null);
  const [processingSubprompt, setProcessingSubprompt] = useState(false);
  const [loadingPromptData, setLoadingPromptData] = useState(false);
  const [promptDisplayText, setPromptDisplayText] = useState<string | null>(null);

  // Reset processed text and selected subprompt when tab or transcription changes
  useEffect(() => {
    setProcessedPromptText(null);
    setSelectedSubprompt(null);
  }, [activeTab, transcribedText]);

  // Effect to process the prompt text when a tab changes or a transcription is available
  useEffect(() => {
    // Only run for prompt tabs (index 2 and above, after Original Transcription and Summary)
    if (activeTab >= 2 &&
      activeTab < globalPrompts.filter(prompt => prompt.enabled).length + 2 &&
      transcribedText) {

      setLoadingPromptData(true);
      setPromptDisplayText(null);

      // Function to process the prompt
      const processPrompt = async () => {
        try {
          const token = await getToken();
          const userId = localStorage.getItem('clerk-user-id');

          if (!token || !userId) {
            throw new Error('Authentication required');
          }

          // Adjust index to account for original transcript (index 0) and summary (index 1)
          const currentPrompt = globalPrompts.filter(prompt => prompt.enabled)[activeTab - 2];

          // Replace placeholders in the prompt content
          const processedContent = currentPrompt.promptContent.replace(
            /{{transcription}}/g,
            transcribedText
          );

          const response = await fetch(`${API_URL}/prompts/process`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-clerk-user-id': userId
            },
            body: JSON.stringify({
              promptContent: processedContent,
              promptId: currentPrompt.id
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to process prompt: ${response.statusText}`);
          }

          const data = await response.json();
          setPromptDisplayText(data.result);
        } catch (error) {
          console.error('Error processing prompt:', error);
          setPromptDisplayText("Error processing prompt. Please try again.");
          showErrorToast(`Failed to process prompt: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
          setLoadingPromptData(false);
        }
      };

      // Process the prompt
      processPrompt();
    }
  }, [activeTab, transcribedText, getToken]);

  // Process subprompt
  const processSubPrompt = async (subprompt: SubPrompt) => {
    if (!transcribedText) return;

    setProcessingSubprompt(true);
    setProcessedPromptText(null); // Clear previous text while loading

    try {
      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      if (!token || !userId) {
        throw new Error('Authentication required');
      }

      // Replace placeholders in the prompt content
      const processedContent = subprompt.promptContent.replace(/{{transcription}}/g, transcribedText);

      const response = await fetch(`${API_URL}/prompts/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-clerk-user-id': userId
        },
        body: JSON.stringify({
          promptContent: processedContent,
          promptId: subprompt.globalPromptId,
          subPromptId: subprompt.id
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to process subprompt: ${response.statusText}`);
      }

      const data = await response.json();
      setProcessedPromptText(data.result);
      setSelectedSubprompt(subprompt);
    } catch (error) {
      console.error('Error processing subprompt:', error);
      showErrorToast(`Failed to process subprompt: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingSubprompt(false);
    }
  };

  // Add state for transcription editing
  const [isEditingTranscription, setIsEditingTranscription] = useState(false);
  const [editedTranscription, setEditedTranscription] = useState<string>("");
  const [updatingTranscription, setUpdatingTranscription] = useState(false);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);
  const [editedPromptTexts, setEditedPromptTexts] = useState<{ [key: number]: string }>({});

  // Add effect to initialize edited transcription when transcribed text changes
  useEffect(() => {
    if (transcribedText) {
      setEditedTranscription(transcribedText);
    }
  }, [transcribedText]);

  const handleSaveTranscription = async () => {
    if (!transcribedText || !editedTranscription) {
      showWarningToast("No content to save");
      return;
    }

    try {
      setUpdatingTranscription(true);

      // If we have a transcription ID, use updateTranscription endpoint
      if (transcriptionId) {
        // Update existing transcription
        const response = await updateTranscription({
          id: transcriptionId,
          transcription: editedTranscription
        }).unwrap();

        // Update the transcription state
        setTranscribedText(response.textContent || editedTranscription);
        setIsEditingTranscription(false);

        showSuccessToast("Transcription updated successfully!");
      } else {
        // Fallback to creating new transcription if no ID is available
        const token = await getToken();
        const userId = localStorage.getItem('clerk-user-id');

        if (!token || !userId) {
          throw new Error('Authentication required');
        }

        const response = await processText({
          text: editedTranscription,
          duration: elapsedTimeRef.current || 0
        }).unwrap();

        // Update the transcription state
        setTranscribedText(response.transcribedText);

        // Store new transcription ID if available
        if (response.transcriptionId) {
          setTranscriptionId(response.transcriptionId);
        }

        setIsEditingTranscription(false);

        showSuccessToast("New transcription created successfully!");
      }

      // Refetch user data to update transcription count
      refetchUserData();

    } catch (error: any) {
      console.error('Error saving edited transcription:', error);
      showErrorToast(error.message || 'Failed to save edited transcription');
    } finally {
      setUpdatingTranscription(false);
    }
  };

  // Helper function to detect browser name
  const getBrowserName = () => {
    const userAgent = navigator.userAgent;

    if (userAgent.match(/chrome|chromium|crios/i)) {
      return 'Chrome';
    } else if (userAgent.match(/firefox|fxios/i)) {
      return 'Firefox';
    } else if (userAgent.match(/safari/i)) {
      return 'Safari';
    } else if (userAgent.match(/edg/i)) {
      return 'Edge';
    } else {
      return 'Unknown';
    }
  };

  // Helper function to get browser-specific permission reset instructions
  const getPermissionInstructions = (browserName: string) => {
    switch (browserName) {
      case 'Chrome':
        return 'Click the info icon (ⓘ) or three dots (⋮) in the address bar → Site settings → Reset permissions';
      case 'Firefox':
        return 'Click the shield or lock icon in the address bar → Connection secure → More information → Permissions → Reset permissions';
      case 'Safari':
        return 'Go to Safari → Settings → Websites → Microphone → Find this website and change the permission';
      case 'Edge':
        return 'Click the info icon (ⓘ) or lock icon in the address bar → Site permissions → Reset permissions';
      default:
        return 'Please reset microphone permissions for this website in your browser settings';
    }
  };

  // Helper function to get all tabs data
  const getAllTabs = () => {
    const tabs = [];

    // Original Transcription tab
    tabs.push({
      index: 0,
      title: "Transcription",
      icon: <FontAwesomeIcon icon={faStickyNote} size="lg" />,
      type: 'transcription'
    });

    // Summary tab
    tabs.push({
      index: 1,
      title: "Summary",
      icon: <FontAwesomeIcon icon={faFileAlt} size="lg" />,
      type: 'summary'
    });

    // Dynamic prompt tabs
    globalPrompts.filter(prompt => prompt.enabled).forEach((prompt, index) => {
      tabs.push({
        index: index + 2,
        title: prompt.title,
        icon: getPromptIcon(prompt.iconType),
        type: 'prompt',
        promptData: prompt
      });
    });

    // Only add Slack and Salesforce tabs if not on carehub domain
    if (!isCareHubDomain) {
      // Slack tab
      const slackIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;
      tabs.push({
        index: slackIndex,
        title: "Share on Slack",
        icon: <SlackIcon size={24} />,
        type: 'slack'
      });

      // Salesforce tab
      const salesforceIndex = globalPrompts.filter(prompt => prompt.enabled).length + 3;
      tabs.push({
        index: salesforceIndex,
        title: "Send to Salesforce",
        icon: <SalesforceIcon size={24} />,
        type: 'salesforce'
      });
    }

    return tabs;
  };

  // Handle "More" menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  // Handle "More" menu close
  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };

  // Handle selecting a tab from the "More" menu
  const handleMoreTabSelect = (tabIndex: number) => {
    // Check if user is trying to access Slack or Salesforce tab when disabled
    const slackTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 2;
    const salesforceTabIndex = globalPrompts.filter(prompt => prompt.enabled).length + 3;

    if (!isCareHubDomain) {
      if (tabIndex === slackTabIndex && !isSlackEnabled) {
        setBlockedFeature('Slack Integration');
        setUpgradeModalOpen(true);
        setMoreMenuAnchorEl(null);
        return; // Don't change tab
      }

      if (tabIndex === salesforceTabIndex && !isSalesforceEnabled) {
        setBlockedFeature('Salesforce Integration');
        setUpgradeModalOpen(true);
        setMoreMenuAnchorEl(null);
        return; // Don't change tab
      }
    }

    setActiveTab(tabIndex);
    setMoreMenuAnchorEl(null);
    // Reset subprompt selection when changing tabs
    setSelectedSubprompt(null);
    setProcessedPromptText(null);
  };

  // Handle editing prompt results
  const handleEditPromptResult = (promptIndex: number, currentText: string) => {
    setEditingPromptIndex(promptIndex);
    setEditedPromptTexts(prev => ({
      ...prev,
      [promptIndex]: currentText
    }));
  };

  // Handle saving prompt edit
  const handleSavePromptEdit = (promptIndex: number) => {
    setEditingPromptIndex(null);
    // The edited text remains in state for download purposes
  };

  // Handle canceling prompt edit
  const handleCancelPromptEdit = () => {
    setEditingPromptIndex(null);
  };

  // Get the display text for a prompt (edited or original)
  const getPromptDisplayText = (promptIndex: number, originalText: string) => {
    return editedPromptTexts[promptIndex] || originalText;
  };

  // Handle opening contact form
  const handleOpenContactForm = (featureName: string) => {
    setContactFormData({
      name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
      email: user?.email || '',
      message: '',
      feature: featureName
    });
    setUpgradeModalOpen(false);
    setContactFormOpen(true);
  };

  // Handle contact form field changes
  const handleContactFormChange = (field: string, value: string) => {
    setContactFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle contact form submission
  const handleContactFormSubmit = async () => {
    if (!contactFormData.name || !contactFormData.email) {
      showWarningToast("Please fill in your name and email");
      return;
    }

    try {
      setSendingContactForm(true);

      const token = await getToken();
      const userId = localStorage.getItem('clerk-user-id');

      // Include company name from settings
      const formDataWithCompany = {
        ...contactFormData,
        company: settings.companyName || 'Tranzcribe'
      };

      const response = await fetch(`${API_URL}/contact/professional-license`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(userId && { 'x-clerk-user-id': userId })
        },
        body: JSON.stringify(formDataWithCompany)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send inquiry');
      }

      showSuccessToast(data.message || "Your inquiry has been sent successfully!");
      setContactFormOpen(false);
      setContactFormData({
        name: '',
        email: '',
        message: '',
        feature: ''
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      showErrorToast(`Failed to send inquiry: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingContactForm(false);
    }
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh"
    }}>
      <Header />
      <Box
        sx={{
          textAlign: "center",
          color: "#333333",
          width: "100%",
          flexGrow: 1,
          overflowY: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 2,
          pb: 4
        }}
      >
        {/* Spinner overlay when processing audio */}
        {isProcessingAudio && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(255,255,255,0.5)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={80} />
          </Box>
        )}

        {/* Add toggle switch for input mode */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: '24px',
            padding: '4px 12px',
            backgroundColor: '#f5f5f5'
          }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 1,
                opacity: isTextMode ? 0.5 : 1,
                transition: 'opacity 0.3s',
                cursor: 'pointer',
                color: !isTextMode ? (settings.primaryColor || '#1976d2') : 'inherit'
              }}
              onClick={() => setIsTextMode(false)}
            >
              <MicIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ mr: 1, fontWeight: !isTextMode ? 'medium' : 'normal' }}>
                Audio Recording
              </Typography>
            </Box>

            <Switch
              checked={isTextMode}
              onChange={handleModeToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: settings.primaryColor || '#1976d2',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: settings.primaryColor || '#1976d2',
                },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 1,
                opacity: !isTextMode ? 0.5 : 1,
                transition: 'opacity 0.3s',
                cursor: 'pointer',
                color: isTextMode ? (settings.primaryColor || '#1976d2') : 'inherit'
              }}
              onClick={() => setIsTextMode(true)}
            >
              <TextFieldsIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: isTextMode ? 'medium' : 'normal' }}>
                Text Input
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={!isTextMode ? handleMicClick : undefined}
          sx={{ textAlign: "center", mb: 4, cursor: !isTextMode ? "pointer" : "default", mt: 4 }}
        >
          {hasRecorded && !isRecording && transcribedText && !isTextMode && (
            <Button
              variant="contained"
              sx={{
                borderRadius: '20px',
                py: 1,
                px: 3,
                backgroundColor: settings.primaryColor || '#1976d2',
                '&:hover': {
                  backgroundColor: settings.secondaryColor || '#1565c0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MicIcon />
                <Typography>Record a new message</Typography>
              </Box>
            </Button>
          )}

          {hasRecorded && transcribedText && isTextMode && (
            <Button
              variant="contained"
              onClick={() => handleMicClick()}
              sx={{
                borderRadius: '20px',
                py: 1,
                px: 3,
                backgroundColor: settings.primaryColor || '#1976d2',
                '&:hover': {
                  backgroundColor: settings.secondaryColor || '#1565c0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextFieldsIcon />
                <Typography>Enter new text</Typography>
              </Box>
            </Button>
          )}
        </Box>

        {/* Text input mode UI */}
        {isTextMode && !transcribedText && (
          <Box sx={{ width: '80%', maxWidth: '800px', mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Enter your text
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={12}
                variant="outlined"
                value={directTextInput}
                onChange={(e) => setDirectTextInput(e.target.value)}
                placeholder="Type or paste your text here..."
                sx={{ mb: 2 }}
                onKeyDown={(e) => {
                  // Submit on Ctrl+Enter or Cmd+Enter
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleTextSubmit();
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Press Ctrl+Enter to submit
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleTextSubmit}
                  startIcon={<SendIcon />}
                  sx={{
                    backgroundColor: settings.primaryColor || '#1976d2',
                    '&:hover': {
                      backgroundColor: settings.secondaryColor || '#1565c0',
                    }
                  }}
                >
                  Submit Text
                </Button>
              </Box>
            </Paper>
          </Box>
        )}

        {!hasRecorded && countdown === null && !isRecording && !isTextMode && (
          <Box mt={20} mb={8}>
            <IconButton
              onClick={startCountdown}
              sx={{
                mb: 2,
                transform: "scale(5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 auto",
                width: "fit-content",
                border: `2px solid ${settings.primaryColor || '#1976d2'}`,
                backgroundColor: `rgba(${settings.primaryColor ? parseInt(settings.primaryColor.slice(1, 3), 16) : 25}, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(3, 5), 16) : 118
                  }, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(5, 7), 16) : 210}, 0.1)`,
                boxShadow: `0px 0px 10px rgba(${settings.primaryColor ? parseInt(settings.primaryColor.slice(1, 3), 16) : 25}, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(3, 5), 16) : 118
                  }, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(5, 7), 16) : 210}, 0.3)`,
                "&:hover": {
                  backgroundColor: `rgba(${settings.primaryColor ? parseInt(settings.primaryColor.slice(1, 3), 16) : 25}, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(3, 5), 16) : 118
                    }, ${settings.primaryColor ? parseInt(settings.primaryColor.slice(5, 7), 16) : 210}, 0.2)`,
                },
              }}
            >
              <MicIcon
                fontSize="large"
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  color: settings.primaryColor || '#1976d2',
                }}
              />
            </IconButton>
            <Typography sx={{ mt: 6, color: "#666", fontWeight: "medium" }}>
              Click to start recording
            </Typography>
          </Box>
        )}

        {countdown !== null ? (
          <Box
            display="flex"
            flexDirection="column"
            sx={updatedCountdownBoxStyles}
            onClick={() => {
              setCountdown(null);
            }}
          >
            <Typography variant="h1" sx={updatedCountdownTypographyStyles}>
              {countdown}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "medium",
                mt: 1,
                color: "white",
              }}
            >
              Stop
            </Typography>
          </Box>
        ) : isRecording ? (
          <Box sx={{ mt: 10, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                color: elapsedTime >= 590 ? "red" : elapsedTime >= 300 ? "#ff9800" : "red",
                fontSize: "32px",
                mb: 8,
                fontWeight: elapsedTime >= 590 ? "bold" : "normal"
              }}
            >
              {formatElapsedTime(elapsedTime)}
              {elapsedTime >= 300 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontSize: '14px',
                    color: elapsedTime >= 590 ? "red" : "#ff9800",
                    mt: 1
                  }}
                >
                  {elapsedTime >= 590
                    ? "Recording will stop in " + (600 - elapsedTime) + " seconds!"
                    : "Max duration: 10 minutes"}
                </Typography>
              )}
            </Typography>

            <IconButton
              color="secondary"
              onClick={stopRecording}
              sx={{
                transform: "scale(3)",
                backgroundColor: "rgba(211, 47, 47, 0.1)",
                border: "2px solid #d32f2f",
                boxShadow: "0px 0px 10px rgba(211, 47, 47, 0.3)",
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.2)",
                },
              }}
            >
              <StopIcon fontSize="medium" />
            </IconButton>

            <Typography sx={{ mt: 8, color: "#666" }}>
              Click to stop recording
            </Typography>
          </Box>
        ) : showCompletedIcon ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <ThumbUpIcon color="success" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ color: "green" }}>
              Completed!
            </Typography>
          </Box>
        ) : null}

        {isProcessingAudio || isProcessingText && (
          <LoadingPage />
        )}

        {loadingSummary && <LoadingPage />}

        {transcribedText && (
          <Box sx={{
            width: '100%',
            maxWidth: '1200px',
            mt: 4,
            "& .MuiBox-root": {
              textAlign: "left" // Make sure all child Box components are left-aligned
            }
          }}>
            {/* Tab Navigation */}
            <Box sx={{
              mb: 2,
              mt: 1,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0
            }}>
              {(() => {
                const allTabs = getAllTabs();
                // On mobile, show only 1 tab directly and put rest in "More" menu
                // On desktop, show 5 tabs directly  
                const maxVisibleTabs = isMobileView ? 1 : 5;
                const visibleTabs = allTabs.slice(0, maxVisibleTabs);
                const hiddenTabs = allTabs.slice(maxVisibleTabs);

                return (
                  <>
                    <Tabs
                      value={activeTab < maxVisibleTabs ? activeTab : false}
                      onChange={handleTabChange}
                      variant="standard"
                      textColor="primary"
                      indicatorColor="primary"
                      sx={{
                        '& .MuiTabs-indicator': {
                          backgroundColor: settings.primaryColor || '#1976d2',
                        },
                        '& .Mui-selected': {
                          color: settings.primaryColor || '#1976d2',
                        },
                        '& .MuiTabs-flexContainer': {
                          gap: 0
                        }
                      }}
                    >
                      {visibleTabs.map((tab) => (
                        <Tab
                          key={tab.index}
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              {tab.icon}
                              {!isMobileView && <Typography sx={{ textAlign: 'center' }}>{tab.title}</Typography>}
                            </Box>
                          }
                          sx={{
                            textTransform: 'none',
                            width: isMobileView ? '80px' : '200px',
                            minWidth: isMobileView ? '80px' : '200px',
                            maxWidth: isMobileView ? '80px' : '200px'
                          }}
                          aria-label={tab.title}
                        />
                      ))}
                    </Tabs>

                    {hiddenTabs.length > 0 && (
                      <Box
                        onClick={handleMoreMenuOpen}
                        sx={{
                          width: isMobileView ? '80px' : '100px',
                          minWidth: isMobileView ? '80px' : '100px',
                          maxWidth: isMobileView ? '80px' : '100px',
                          height: '48px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          textTransform: 'none',
                          borderBottom: activeTab >= maxVisibleTabs ? `2px solid ${settings.primaryColor || '#1976d2'}` : '2px solid transparent',
                          color: activeTab >= maxVisibleTabs ? (settings.primaryColor || '#1976d2') : '#666',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            color: settings.primaryColor || '#1976d2',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          },
                          fontSize: '0.875rem',
                          fontWeight: activeTab >= maxVisibleTabs ? 500 : 400,
                          position: 'relative'
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: isMobileView ? '20px' : '28px' }} />
                        {isMobileView ? "Options" : "More"}
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>

            {/* More Menu */}
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={handleMoreMenuClose}
              PaperProps={{
                sx: {
                  maxHeight: 400,
                  width: isMobileView ? 280 : 350,
                  minWidth: isMobileView ? 280 : 350
                }
              }}
            >
              {(() => {
                const allTabs = getAllTabs();
                const maxVisibleTabs = isMobileView ? 1 : 4;
                const hiddenTabs = allTabs.slice(maxVisibleTabs);

                return hiddenTabs.map((tab) => (
                  <MenuItem
                    key={tab.index}
                    onClick={() => handleMoreTabSelect(tab.index)}
                    selected={activeTab === tab.index}
                    sx={{
                      minHeight: '48px',
                      py: 1.5,
                      px: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box sx={{ flexShrink: 0 }}>
                        {tab.icon}
                      </Box>
                      <Typography sx={{
                        flexGrow: 1,
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        lineHeight: 1.3,
                        fontSize: '0.875rem'
                      }}>
                        {tab.title}
                      </Typography>
                    </Box>
                  </MenuItem>
                ));
              })()}
            </Menu>

            {globalPromptsLoading ? (
              <CircularProgress />
            ) : (
              <>
                {/* Original Transcription Tab */}
                {activeTab === 0 && transcribedText !== null && (
                  <Box
                    sx={{
                      width: { xs: '95%', sm: '90%', md: '80%' },
                      backgroundColor: "white",
                      borderRadius: "12px",
                      padding: { xs: '16px', sm: '24px' },
                      marginBottom: "20px",
                      color: "#333",
                      maxHeight: "500px",
                      overflow: "auto",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      mx: "auto",
                      textAlign: "left"
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Original Transcription</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isEditingTranscription ? (
                          <>
                            <Button
                              onClick={() => copyToClipboard(transcribedText)}
                              sx={{
                                textTransform: "none",
                                color: "#333",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.05)",
                                },
                                padding: "4px 8px",
                                minWidth: "unset",
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }}
                              startIcon={<ContentCopyIcon fontSize="small" />}
                            >
                              Copy
                            </Button>
                            <Button
                              onClick={() => setIsEditingTranscription(true)}
                              variant="outlined"
                              startIcon={<EditIcon />}
                              sx={{
                                borderColor: settings.primaryColor || '#1976d2',
                                color: settings.primaryColor || '#1976d2',
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                '&:hover': {
                                  borderColor: settings.secondaryColor || '#1565c0',
                                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                },
                              }}
                            >
                              Edit
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={handleSaveTranscription}
                            variant="outlined"
                            disabled={updatingTranscription}
                            startIcon={updatingTranscription ? <CircularProgress size={16} /> : <SaveIcon />}
                            sx={{
                              borderColor: settings.primaryColor || '#1976d2',
                              color: settings.primaryColor || '#1976d2',
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              '&:hover': {
                                borderColor: settings.secondaryColor || '#1565c0',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              },
                            }}
                          >
                            {updatingTranscription ? "Saving..." : "Save"}
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {isEditingTranscription ? (
                      <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={12}
                        variant="outlined"
                        value={editedTranscription}
                        onChange={(e) => setEditedTranscription(e.target.value)}
                        sx={{
                          mb: 2,
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.85rem', sm: '1rem' }
                          }
                        }}
                      />
                    ) : (
                      <Typography sx={{
                        whiteSpace: "pre-line",
                        textAlign: "left",
                        fontSize: { xs: '0.85rem', sm: '1rem' }
                      }}>
                        {transcribedText}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Summary Tab */}
                {activeTab === 1 && summarizedText !== null && (
                  <Box
                    sx={{
                      width: { xs: '95%', sm: '90%', md: '80%' }, // Wider on mobile
                      backgroundColor: "white",
                      borderRadius: "12px",
                      padding: { xs: '16px', sm: '24px' }, // Less padding on mobile
                      marginBottom: "20px",
                      color: "#333",
                      maxHeight: "500px",
                      overflow: "auto",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      mx: "auto",
                      textAlign: "left" // Explicitly set summary text to be left-aligned
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Summary</Typography>
                      <Button
                        onClick={() => copyToClipboard(summarizedText)}
                        sx={{
                          textTransform: "none",
                          color: "#333",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.05)",
                          },
                          padding: "4px 8px",
                          minWidth: "unset",
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }}
                        startIcon={<ContentCopyIcon fontSize="small" />}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Typography sx={{
                      whiteSpace: "pre-line",
                      textAlign: "left",
                      fontSize: { xs: '0.85rem', sm: '1rem' }
                    }}>
                      {summarizedText}
                    </Typography>
                  </Box>
                )}

                {/* Prompt Tabs */}
                {globalPrompts.filter(prompt => prompt.enabled).map((prompt, index) =>
                  activeTab === index + 2 ? (
                    <Box
                      key={prompt.id}
                      sx={{
                        width: { xs: '95%', sm: '90%', md: '80%' },
                        backgroundColor: "white",
                        borderRadius: "12px",
                        padding: { xs: '16px', sm: '24px' },
                        marginBottom: "20px",
                        color: "#333",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        mx: "auto",
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* Custom prompt display with consistent styling */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: 'transparent',
                        flexGrow: 1,
                        p: 0
                      }}>
                        {/* Header with title and actions */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: { xs: 1, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}>
                          <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: { xs: 'center', sm: 'left' } }}>
                            {selectedSubprompt?.title || prompt.title}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              sx={{
                                textTransform: "none",
                                color: settings.primaryColor || "#333",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.05)",
                                },
                                padding: "4px 8px",
                                minWidth: "unset",
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }}
                              startIcon={<ContentCopyIcon fontSize="small" />}
                              onClick={() => {
                                const currentPromptIndex = index + 2;
                                const textToCopy = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || "");
                                copyToClipboard(textToCopy);
                              }}
                            >
                              Copy
                            </Button>
                            <Button
                              color="inherit"
                              sx={{
                                textTransform: "none",
                                color: settings.primaryColor || "#333",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.05)",
                                },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }}
                              onClick={() => {
                                const currentPromptIndex = index + 2;
                                const textToDownload = getPromptDisplayText(currentPromptIndex, processedPromptText || promptDisplayText || "");
                                handleDownload(
                                  textToDownload,
                                  `transcribe_${prompt.title.toLowerCase().replace(/\s+/g, "-")}.pdf`
                                );
                              }}
                            >
                              Download
                            </Button>
                            {editingPromptIndex === index + 2 ? (
                              <Button
                                variant="outlined"
                                startIcon={<SaveIcon />}
                                sx={{
                                  borderColor: settings.primaryColor || '#1976d2',
                                  color: settings.primaryColor || '#1976d2',
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  '&:hover': {
                                    borderColor: settings.secondaryColor || '#1565c0',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                }}
                                onClick={() => handleSavePromptEdit(index + 2)}
                              >
                                Save
                              </Button>
                            ) : (
                              <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                sx={{
                                  borderColor: settings.primaryColor || '#1976d2',
                                  color: settings.primaryColor || '#1976d2',
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  '&:hover': {
                                    borderColor: settings.secondaryColor || '#1565c0',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                }}
                                onClick={() => handleEditPromptResult(index + 2, processedPromptText || promptDisplayText || "")}
                              >
                                Edit
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {/* Use a hidden PromptTab to ensure compatibility */}
                        <Box sx={{ display: 'none' }}>
                          <PromptTab
                            prompt={prompt}
                            inputText={transcribedText || ""}
                            handleDownload={handleDownload}
                            overrideText={processedPromptText}
                            overrideTitle={selectedSubprompt?.title}
                          />
                        </Box>

                        {/* Display content in our standardized format */}
                        {editingPromptIndex === index + 2 ? (
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={16}
                            variant="outlined"
                            value={editedPromptTexts[index + 2] || ""}
                            onChange={(e) => setEditedPromptTexts(prev => ({
                              ...prev,
                              [index + 2]: e.target.value
                            }))}
                            sx={{
                              mb: 2,
                              flexGrow: 1
                            }}
                          />
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              p: { xs: 1.5, sm: 2, md: 3 },
                              backgroundColor: '#f9f9f9',
                              borderRadius: '8px',
                              flexGrow: 1,
                              height: 'auto',
                              minHeight: '100px',
                              position: 'relative'
                            }}
                          >
                            {(loadingPromptData || processingSubprompt) && (
                              <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(249,249,249,0.7)',
                                zIndex: 1,
                                borderRadius: '8px'
                              }}>
                                <CircularProgress size={40} sx={{ color: settings.primaryColor || '#1976d2' }} />
                              </Box>
                            )}
                            <Typography
                              variant="body1"
                              sx={{
                                whiteSpace: 'pre-line',
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                lineHeight: 1.6
                              }}
                            >
                              {getPromptDisplayText(index + 2, processedPromptText || promptDisplayText || "Processing your request...")}
                            </Typography>
                          </Paper>
                        )}
                      </Box>

                      {/* Display subprompts if available */}
                      {prompt.subPrompts && prompt.subPrompts.length > 0 && (
                        <Box sx={{
                          width: '100%',
                          mt: 2,
                          mb: 1,
                          px: 0,
                          display: 'flex',
                          overflowX: 'auto',
                          gap: 1,
                          pb: 1,
                          '&::-webkit-scrollbar': {
                            height: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: settings.primaryColor || '#1976d2',
                            borderRadius: '3px',
                          },
                        }}>
                          {prompt.subPrompts
                            .filter(subprompt => subprompt.enabled)
                            .map((subprompt) => (
                              <Button
                                key={subprompt.id}
                                variant={selectedSubprompt?.id === subprompt.id ? "contained" : "outlined"}
                                size="small"
                                disabled={processingSubprompt}
                                startIcon={getPromptIcon(subprompt.iconType)}
                                onClick={() => processSubPrompt(subprompt)}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: '20px',
                                  px: 2,
                                  flexShrink: 0,
                                  ...(selectedSubprompt?.id === subprompt.id
                                    ? {
                                      backgroundColor: settings.primaryColor || '#1976d2',
                                      color: '#fff',
                                      '&:hover': {
                                        backgroundColor: settings.secondaryColor || '#1565c0',
                                      },
                                    }
                                    : {
                                      color: settings.primaryColor || '#1976d2',
                                      borderColor: settings.primaryColor || '#1976d2',
                                      '&:hover': {
                                        borderColor: settings.secondaryColor || '#1565c0',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                      },
                                    }),
                                }}
                              >
                                {subprompt.title}
                                {processingSubprompt && selectedSubprompt?.id === subprompt.id &&
                                  <CircularProgress size={16} sx={{ ml: 1, color: '#fff' }} />}
                              </Button>
                            ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box key={prompt.id} />
                  ),
                )}

                {/* Slack sharing tab content */}
                {!isCareHubDomain && activeTab === globalPrompts.filter(prompt => prompt.enabled).length + 2 && (
                  <Box
                    sx={{
                      width: { xs: '95%', sm: '90%', md: '80%' }, // Wider on mobile
                      backgroundColor: "white",
                      borderRadius: "12px",
                      padding: { xs: '16px', sm: '24px' }, // Less padding on mobile
                      marginBottom: "20px",
                      color: "#333",
                      maxHeight: "500px",
                      overflow: "auto",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      mx: "auto",
                      textAlign: "left",
                      opacity: isSlackEnabled ? 1 : 0.5,
                      userSelect: isSlackEnabled ? 'auto' : 'none'
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Share on Slack</Typography>
                      <Button
                        onClick={handleShareToSlack}
                        variant="contained"
                        disabled={shareToSlackLoading}
                        startIcon={shareToSlackLoading ? <CircularProgress size={20} /> : <SlackIcon size={20} />}
                        sx={{
                          backgroundColor: settings.primaryColor || '#1976d2',
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          '&:hover': {
                            backgroundColor: settings.secondaryColor || '#1565c0',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          opacity: isSlackEnabled ? 1 : 0.6,
                          cursor: isSlackEnabled ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {shareToSlackLoading ? (isMobileView ? "..." : "Sharing...") : (isMobileView ? "Share" : "Share to Slack")}
                      </Button>
                    </Box>
                    <Typography sx={{
                      whiteSpace: "pre-line",
                      textAlign: "left",
                      mb: 3,
                      fontSize: { xs: '0.85rem', sm: '1rem' }
                    }}>
                      Your transcription will be shared to a channel on Slack.
                    </Typography>
                    <Box sx={{
                      p: { xs: 2, sm: 3 },
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}>
                      <Typography variant="body1" sx={{
                        whiteSpace: "pre-line",
                        textAlign: "left",
                        fontSize: { xs: '0.85rem', sm: '1rem' }
                      }}>
                        {transcribedText}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Salesforce sharing tab content */}
                {!isCareHubDomain && activeTab === globalPrompts.filter(prompt => prompt.enabled).length + 3 && (
                  <Box
                    sx={{
                      width: { xs: '95%', sm: '90%', md: '80%' }, // Wider on mobile
                      backgroundColor: "white",
                      borderRadius: "12px",
                      padding: { xs: '16px', sm: '24px' }, // Less padding on mobile
                      marginBottom: "20px",
                      color: "#333",
                      maxHeight: "500px",
                      overflow: "auto",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      mx: "auto",
                      textAlign: "left",
                      opacity: isSalesforceEnabled ? 1 : 0.5,
                      userSelect: isSalesforceEnabled ? 'auto' : 'none'
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography variant="h6" color="#333" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Send to Salesforce</Typography>
                      <Button
                        onClick={handleShareToSalesforce}
                        variant="contained"
                        disabled={shareToSalesforceLoading}
                        startIcon={shareToSalesforceLoading ? <CircularProgress size={20} /> : <SalesforceIcon size={20} />}
                        sx={{
                          backgroundColor: settings.primaryColor || '#1976d2',
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          '&:hover': {
                            backgroundColor: settings.secondaryColor || '#1565c0',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          opacity: isSalesforceEnabled ? 1 : 0.6,
                          cursor: isSalesforceEnabled ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {shareToSalesforceLoading ? (isMobileView ? "..." : "Sending...") : (isMobileView ? "Send" : "Send to Salesforce")}
                      </Button>
                    </Box>
                    <Typography sx={{
                      whiteSpace: "pre-line",
                      textAlign: "left",
                      mb: 3,
                      fontSize: { xs: '0.85rem', sm: '1rem' }
                    }}>
                      Your transcription will be sent to Salesforce with your email: {user?.email || 'No email found'}
                    </Typography>

                    <Box sx={{
                      p: { xs: 2, sm: 3 },
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}>
                      <Typography variant="body1" sx={{
                        whiteSpace: "pre-line",
                        textAlign: "left",
                        fontSize: { xs: '0.85rem', sm: '1rem' }
                      }}>
                        {transcribedText}
                      </Typography>
                    </Box>

                    {salesforceError && (
                      <Alert
                        severity="error"
                        sx={{
                          mt: 2,
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }
                        }}
                        onClose={() => setSalesforceError(null)}
                      >
                        {salesforceError}
                      </Alert>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
      <Footer />

      {/* Upgrade Modal */}
      <Dialog
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
          Professional License Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Dear customer, this option is available in our professional license.{' '}
            <Typography
              component="span"
              onClick={() => handleOpenContactForm(blockedFeature || 'Slack Integration')}
              sx={{
                color: settings.primaryColor || '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 'medium',
                '&:hover': {
                  color: settings.secondaryColor || '#1565c0',
                }
              }}
            >
              Click here if you want more information.
            </Typography>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setUpgradeModalOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: settings.primaryColor || '#1976d2',
              '&:hover': {
                backgroundColor: settings.secondaryColor || '#1565c0',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Form Modal */}
      <Dialog
        open={contactFormOpen}
        onClose={() => !sendingContactForm && setContactFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
          Request Professional License Information
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Please fill out the form below and we'll get back to you shortly.
          </Typography>

          <TextField
            fullWidth
            label="Name *"
            value={contactFormData.name}
            onChange={(e) => handleContactFormChange('name', e.target.value)}
            margin="normal"
            disabled={sendingContactForm}
          />

          <TextField
            fullWidth
            label="Email *"
            type="email"
            value={contactFormData.email}
            onChange={(e) => handleContactFormChange('email', e.target.value)}
            margin="normal"
            disabled={sendingContactForm}
          />

          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={contactFormData.message}
            onChange={(e) => handleContactFormChange('message', e.target.value)}
            margin="normal"
            placeholder="Tell us about your needs..."
            disabled={sendingContactForm}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setContactFormOpen(false)}
            disabled={sendingContactForm}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContactFormSubmit}
            variant="contained"
            disabled={sendingContactForm}
            startIcon={sendingContactForm ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: settings.primaryColor || '#1976d2',
              '&:hover': {
                backgroundColor: settings.secondaryColor || '#1565c0',
              }
            }}
          >
            {sendingContactForm ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudioTranscriber;
